import { type MeResponse, type OpsMe, PARTNER_TENANT_ID, type PartnerMe } from "@kontaktfix/shared";
import { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { AppShell } from "@/layout/app-shell";
import { ApiError, api } from "@/lib/api";
import { keys } from "@/lib/queries";
import { opsKeys } from "@/ops/queries";
import { ActivityPage } from "@/pages/activity";
import { ConnectOwnAppPage } from "@/pages/connect-own-app";
import { ContactsPage } from "@/pages/contacts";
import { HomePage } from "@/pages/home";
import { ImportPage } from "@/pages/import";
import { ListDetailPage } from "@/pages/list-detail";
import { ListsPage } from "@/pages/lists";
import { LoginPage } from "@/pages/login";
import { MaintenancePage } from "@/pages/maintenance";
import { NotificationsPage } from "@/pages/notifications";
import { PeoplePage, PersonPage } from "@/pages/people";
import { PhoneGuidePage } from "@/pages/phone-guide";
import { SettingsPage } from "@/pages/settings";
import { SyncPage } from "@/pages/sync";
import { SyncRunPage } from "@/pages/sync-run";
import { UnsubscribePage } from "@/pages/unsubscribe";
import { WelcomePage } from "@/pages/welcome";

export const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 15_000, refetchOnWindowFocus: true, retry: 1 } },
});

async function loadMe(): Promise<MeResponse | null> {
  try {
    return await queryClient.ensureQueryData({
      queryKey: keys.me,
      queryFn: () => api.get<MeResponse>("/api/me"),
    });
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return null;
    throw err;
  }
}

async function loadOpsMe(): Promise<OpsMe | null> {
  try {
    return await queryClient.ensureQueryData({
      queryKey: opsKeys.me,
      queryFn: () => api.get<OpsMe>("/api/ops/auth/me"),
    });
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return null;
    throw err;
  }
}

const rootRoute = createRootRouteWithContext<{ queryClient: QueryClient }>()({ component: Outlet });

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: (s: Record<string, unknown>): { error?: string } => ({
    error: typeof s.error === "string" ? s.error : undefined,
  }),
  beforeLoad: async () => {
    const me = await loadMe();
    if (me) throw redirect({ to: "/" });
  },
  component: LoginPage,
});

const maintenanceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/maintenance",
  component: MaintenancePage,
});

const unsubscribeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/unsubscribe",
  component: UnsubscribePage,
});

const welcomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/welcome",
  beforeLoad: async () => {
    const me = await loadMe();
    if (!me) throw redirect({ to: "/login" });
    if (me.org) throw redirect({ to: "/" });
  },
  component: WelcomePage,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  beforeLoad: async () => {
    const me = await loadMe();
    if (!me) throw redirect({ to: "/login" });
    // A partner whose access ended goes back to the portal, not to the customer onboarding.
    if (!me.org && me.identity.tenantId === PARTNER_TENANT_ID) throw redirect({ to: "/partner/customers" });
    if (!me.org) throw redirect({ to: "/welcome" });
  },
  component: AppShell,
});

const r = <P extends string>(path: P, component: () => React.ReactNode) =>
  createRoute({ getParentRoute: () => appRoute, path, component });

const syncRunRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/sync/runs/$runId",
  validateSearch: (s: Record<string, unknown>): { tab?: string } => ({
    tab: typeof s.tab === "string" ? s.tab : undefined,
  }),
  component: SyncRunPage,
});

const listsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/lists",
  // ?new=file opens the new list dialog with that kind (from the integrations settings).
  validateSearch: (s: Record<string, unknown>): { new?: "manual" | "directory" | "file" } => ({
    new: s.new === "file" || s.new === "directory" || s.new === "manual" ? s.new : undefined,
  }),
  component: ListsPage,
});

const contactsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/contacts",
  validateSearch: (
    s: Record<string, unknown>,
  ): { q?: string; listId?: string; page?: number; contact?: string } => ({
    q: typeof s.q === "string" ? s.q : undefined,
    listId: typeof s.listId === "string" ? s.listId : undefined,
    page: typeof s.page === "number" ? s.page : undefined,
    contact: typeof s.contact === "string" ? s.contact : undefined,
  }),
  component: ContactsPage,
});

const listDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/lists/$listId",
  validateSearch: (s: Record<string, unknown>): { tab?: string; contact?: string } => ({
    tab: typeof s.tab === "string" ? s.tab : undefined,
    contact: typeof s.contact === "string" ? s.contact : undefined,
  }),
  component: ListDetailPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/{-$tab}",
  validateSearch: (
    s: Record<string, unknown>,
  ): { consent?: string; reason?: string; checkout?: "success" | "cancel" } => ({
    consent: typeof s.consent === "string" ? s.consent : undefined,
    reason: typeof s.reason === "string" ? s.reason : undefined,
    checkout: s.checkout === "success" || s.checkout === "cancel" ? s.checkout : undefined,
  }),
  component: SettingsPage,
});

/* ---------- Operator dashboard (/ops): own sign-in, loaded on demand ---------- */

const opsLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ops/login",
  beforeLoad: async () => {
    if (await loadOpsMe()) throw redirect({ to: "/ops" });
  },
  component: lazyRouteComponent(() => import("@/ops/pages/login"), "OpsLoginPage"),
});

const opsSetupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ops/setup",
  component: lazyRouteComponent(() => import("@/ops/pages/setup"), "OpsSetupPage"),
});

const opsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ops",
  beforeLoad: async () => {
    if (!(await loadOpsMe())) throw redirect({ to: "/ops/login" });
  },
  component: lazyRouteComponent(() => import("@/ops/shell"), "OpsShell"),
});

const o = <P extends string>(path: P, component: ReturnType<typeof lazyRouteComponent>) =>
  createRoute({ getParentRoute: () => opsRoute, path, component });

/* ---------- IT partner portal (/partner): Better Auth sign-in, loaded on demand ---------- */

async function loadPartnerMe(): Promise<PartnerMe | null> {
  try {
    return await queryClient.ensureQueryData({
      queryKey: ["partner", "me"],
      queryFn: () => api.get<PartnerMe>("/api/partner/me"),
    });
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.code === "partner_suspended")) return null;
    throw err;
  }
}

const partnerPublic = (
  path: string,
  name: "PartnerRegisterPage" | "PartnerForgotPasswordPage" | "PartnerResetPasswordPage",
) =>
  createRoute({
    getParentRoute: () => rootRoute,
    path,
    component: lazyRouteComponent(() => import("@/partner/pages/auth"), name),
  });

const partnerLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/partner/login",
  beforeLoad: async () => {
    if (await loadPartnerMe()) throw redirect({ to: "/partner/dashboard" });
  },
  component: lazyRouteComponent(() => import("@/partner/pages/auth"), "PartnerLoginPage"),
});

const partnerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/partner",
  beforeLoad: async ({ location }) => {
    const me = await loadPartnerMe();
    if (!me) throw redirect({ to: "/partner/login" });
    // Nothing but the account page until a second factor protects the account.
    if (!me.user.twoFactorEnabled && !me.user.hasPasskey && location.pathname !== "/partner/account")
      throw redirect({ to: "/partner/account" });
    if (location.pathname === "/partner" || location.pathname === "/partner/")
      throw redirect({ to: "/partner/dashboard" });
  },
  component: lazyRouteComponent(() => import("@/partner/shell"), "PartnerShell"),
});

const p = <P extends string>(path: P, component: ReturnType<typeof lazyRouteComponent>) =>
  createRoute({ getParentRoute: () => partnerRoute, path, component });

const routeTree = rootRoute.addChildren([
  loginRoute,
  maintenanceRoute,
  unsubscribeRoute,
  welcomeRoute,
  appRoute.addChildren([
    r("/", HomePage),
    contactsRoute,
    listsRoute,
    listDetailRoute,
    r("/people", PeoplePage),
    r("/people/$userId", PersonPage),
    r("/sync", SyncPage),
    syncRunRoute,
    r("/activity", ActivityPage),
    r("/import", ImportPage),
    settingsRoute,
    r("/connect/own-app", ConnectOwnAppPage),
    r("/help/phone", PhoneGuidePage),
    r("/account/notifications", NotificationsPage),
  ]),
  partnerLoginRoute,
  partnerPublic("/partner/register", "PartnerRegisterPage"),
  partnerPublic("/partner/forgot-password", "PartnerForgotPasswordPage"),
  partnerPublic("/partner/reset-password", "PartnerResetPasswordPage"),
  partnerRoute.addChildren([
    p(
      "/dashboard",
      lazyRouteComponent(() => import("@/partner/pages/dashboard"), "PartnerDashboardPage"),
    ),
    p(
      "/activity",
      lazyRouteComponent(() => import("@/partner/pages/activity"), "PartnerActivityPage"),
    ),
    p(
      "/billing",
      lazyRouteComponent(() => import("@/partner/pages/billing"), "PartnerBillingPage"),
    ),
    p(
      "/customers",
      lazyRouteComponent(() => import("@/partner/pages/customers"), "PartnerCustomersPage"),
    ),
    p(
      "/team",
      lazyRouteComponent(() => import("@/partner/pages/team"), "PartnerTeamPage"),
    ),
    p(
      "/account",
      lazyRouteComponent(() => import("@/partner/pages/account"), "PartnerAccountPage"),
    ),
  ]),
  opsLoginRoute,
  opsSetupRoute,
  opsRoute.addChildren([
    o(
      "/",
      lazyRouteComponent(() => import("@/ops/pages/overview"), "OpsOverviewPage"),
    ),
    o(
      "/organizations",
      lazyRouteComponent(() => import("@/ops/pages/organizations"), "OpsOrganizationsPage"),
    ),
    o(
      "/organizations/$orgId",
      lazyRouteComponent(() => import("@/ops/pages/organization"), "OpsOrganizationPage"),
    ),
    o(
      "/partners",
      lazyRouteComponent(() => import("@/ops/pages/partners"), "OpsPartnersPage"),
    ),
    o(
      "/partners/$partnerId",
      lazyRouteComponent(() => import("@/ops/pages/partners"), "OpsPartnerPage"),
    ),
    o(
      "/users",
      lazyRouteComponent(() => import("@/ops/pages/users"), "OpsUsersPage"),
    ),
    o(
      "/delivery",
      lazyRouteComponent(() => import("@/ops/pages/delivery"), "OpsDeliveryPage"),
    ),
    o(
      "/graph",
      lazyRouteComponent(() => import("@/ops/pages/graph"), "OpsGraphPage"),
    ),
    o(
      "/emails",
      lazyRouteComponent(() => import("@/ops/pages/emails"), "OpsEmailsPage"),
    ),
    o(
      "/operators",
      lazyRouteComponent(() => import("@/ops/pages/operators"), "OpsOperatorsPage"),
    ),
    o(
      "/audit",
      lazyRouteComponent(() => import("@/ops/pages/audit"), "OpsAuditPage"),
    ),
    o(
      "/events",
      lazyRouteComponent(() => import("@/ops/pages/events"), "OpsEventsPage"),
    ),
    o(
      "/leads",
      lazyRouteComponent(() => import("@/ops/pages/leads"), "OpsLeadsPage"),
    ),
    o(
      "/bug-reports",
      lazyRouteComponent(() => import("@/ops/pages/bug-reports"), "OpsBugReportsPage"),
    ),
    o(
      "/system",
      lazyRouteComponent(() => import("@/ops/pages/system"), "OpsSystemPage"),
    ),
    o(
      "/settings",
      lazyRouteComponent(() => import("@/ops/pages/settings"), "OpsSettingsPage"),
    ),
    o(
      "/account",
      lazyRouteComponent(() => import("@/ops/pages/account"), "OpsAccountPage"),
    ),
  ]),
]);

export const router = createRouter({ routeTree, context: { queryClient }, defaultPreload: "intent" });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
